import dd from 'dedent';
import camelCase from 'lodash.camelcase';
import words from 'lodash.words';
import { formatLocaleNumber } from './i18n.helper';

export const findWords = (input: string): string[] => words(input);

export const findCustomWords = (input: string, pattern: RegExp): string[] =>
  words(input, pattern);

export const matchesTenantName = (
  nameWords: string[],
  tenantName: string | undefined
): boolean => {
  const lower = tenantName?.toLowerCase() ?? '';
  if (!lower) {
    return false;
  }
  if (nameWords.includes(lower)) {
    return true;
  }
  const normalized = lower.replace(/[-_]/g, '');
  return normalized !== lower && nameWords.includes(normalized);
};

export const capitalize = (input: string): string =>
  input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();

/**
 * Jonathan's preferred casing
 *
 * Example:
 * sentenceCase('This Is An Example') -> 'This is an example'
 */
export const sentenceCase = (str: string): string => {
  return findWords(str)
    .map((word, i) => (i === 0 ? capitalize(word) : word.toLowerCase()))
    .join(' ');
};

/**
 * Pascal cases the provided string. Example: ThisIsPascalCase
 */
export const pascalCase = (str: string): string => {
  const camel = camelCase(str);
  return camel.charAt(0).toUpperCase() + camel.slice(1);
};

export const printArrayStringOption = (arr: string[]): string =>
  `[${arr.join(',')}]`;

// biome-ignore lint/suspicious/noExplicitAny: migration from eslint
export const parseJwt = (token: string | undefined): any => {
  if (!token) {
    return null;
  }
  const base64Url = token.split('.').at(1);
  if (!base64Url) {
    return null;
  }
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  try {
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Removes a trailing comma from a string if one exists
 */
export const removeTrailingComma = (str: string): string =>
  str.replace(/,$/g, '');

export const pluralize = (str: string, count: number): string => {
  if (count === 1) {
    return str;
  }
  return `${str}s`;
};

/** `pluralize` can't: the plural is irregular. */
export const repositoriesLabel = (count: number): string =>
  `${formatLocaleNumber(count)} ${count === 1 ? 'repository' : 'repositories'}`;

export const repeatChar = (n: number, str: string): string =>
  Array(n + 1).join(str);

// function to remove all non-alpha-numeric characters and lowercase
export const cleanString = (str: string): string => {
  return str?.replace(/[^0-9a-z]/gi, '-').toLowerCase();
};

const KNOWN_ACRONYMS = new Set(['LST']);

export const screamingSnakeToSentenceCase = (
  str: string | null | undefined
): string => {
  if (!str) {
    return '';
  }
  return str
    .split('_')
    .map((word, i) => {
      if (KNOWN_ACRONYMS.has(word)) {
        return word;
      }
      return i === 0 ? capitalize(word) : word.toLowerCase();
    })
    .join(' ');
};

// function to convert pascal case to sentence case
export const pascalToSentenceCase = (str: string): string => {
  return sentenceCase(
    str
      ?.replace(/([A-Z])/g, ' $1')
      .trim()
      .toLowerCase()
  );
};

// dedent function that returns the dedented version of a string a returns as the generic type
export const dedent = <T extends string>(str: string): T => {
  return dd(str) as T;
};

/**
 * Strips Unicode format characters (variation selectors, zero-width
 * joiners, bidi overrides, etc.) from IdP-supplied display names, which
 * are untrusted and can otherwise render as tofu glyphs or spoof text
 * direction.
 */
export const sanitizeDisplayName = (str: string): string =>
  str.replace(/\p{Cf}/gu, '');

export const convertToMarkdownLink = (
  text: string,
  target: string,
  url: string
): string => {
  const regex = new RegExp(`(?<!\\[)${target}(?!\\]\\([^)]*\\))`, 'g');
  return text.replace(regex, `[${target}](${url})`);
};
